import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

// Analytics event types
export interface AnalyticsEvent {
  id: string;
  event_type: string;
  event_data: Record<string, any>;
  user_id?: string;
  session_id?: string;
  timestamp: Date;
  page_url?: string;
  user_agent?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
}

// Analytics metrics
export interface AnalyticsMetrics {
  pageViews: number;
  uniqueUsers: number;
  sessionDuration: number;
  bounceRate: number;
  conversionRate: number;
  topPages: { page: string; views: number }[];
  userFlow: { from: string; to: string; count: number }[];
  deviceBreakdown: { type: string; count: number; percentage: number }[];
}

export const useAnalytics = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<string | null>(null);

  // Generate session ID
  const generateSessionId = useCallback(() => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Get device type
  const getDeviceType = useCallback((): 'mobile' | 'tablet' | 'desktop' => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }, []);

  // Initialize session
  useEffect(() => {
    const sessionId = generateSessionId();
    setCurrentSession(sessionId);
    
    // Track session start
    trackEvent('session_start', {
      device_type: getDeviceType(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      user_agent: navigator.userAgent,
    });
  }, [generateSessionId, getDeviceType]);

  // Track page views automatically
  useEffect(() => {
    const currentPath = window.location.pathname;
    trackEvent('page_view', {
      page: currentPath,
      referrer: document.referrer,
    });
  }, []);

  // Track event function
  const trackEvent = useCallback(async (
    eventType: string, 
    eventData: Record<string, any> = {},
    options: { persist?: boolean } = { persist: false }
  ) => {
    // Add safety guard for Meta Pixel to avoid TypeError: a.__fbeventsModules[e] is not a function
    try {
      if (typeof window !== 'undefined' && (window as any).fbq && typeof (window as any).fbq === 'function') {
        (window as any).fbq('track', eventType, eventData);
      }
    } catch (e) {
      // Use a low-level warning that doesn't trigger error boundaries or excessive logs
      if (import.meta.env.DEV) console.warn('Meta Pixel tracking skipped (expected in some browsers):', e);
    }

    const event: Omit<AnalyticsEvent, 'id'> = {
      event_type: eventType,
      event_data: {
        ...eventData,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        device_type: getDeviceType(),
      },
      user_id: user?.id,
      session_id: currentSession,
      timestamp: new Date(),
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      device_type: getDeviceType(),
    };

    // Add to local state
    const eventWithId = { ...event, id: crypto.randomUUID() };
    setEvents(prev => [eventWithId, ...prev.slice(0, 99)]);

  // Persist to database if enabled
  if (options.persist) {
    try {
      const { error } = await supabase.from('analytics_events').insert([{
        event_type: event.event_type,
        event_data: event.event_data,
        user_id: event.user_id,
        session_id: event.session_id,
        page_url: event.page_url,
        referrer: document.referrer || null,
        user_agent: event.user_agent,
        device_type: event.device_type,
        ip_address: null // Will be populated by server
      }]);
      
      if (error) {
        // Silently ignore missing table error (PGRST205)
        if (error.code !== 'PGRST205') {
          console.warn('Failed to persist analytics event:', error);
        }
      }
    } catch (error) {
      console.warn('Failed to persist analytics event:', error);
    }
  }
  }, [user?.id, currentSession, getDeviceType]);

  // Specialized tracking functions
  const trackPageView = useCallback((page: string, additionalData?: Record<string, any>) => {
    trackEvent('page_view', { 
      page_url: page,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      ...additionalData 
    }, { persist: false });
  }, [trackEvent]);

  const trackUserAction = useCallback((action: string, target: string, additionalData?: Record<string, any>) => {
    trackEvent('user_action', { 
      action, 
      target,
      page_url: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...additionalData 
    }, { persist: false });
  }, [trackEvent]);

  const trackConversion = useCallback((type: string, value?: number, additionalData?: Record<string, any>) => {
    trackEvent('conversion', { 
      conversion_type: type, 
      value,
      page_url: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...additionalData 
    }, { persist: false });
  }, [trackEvent]);

  const trackError = useCallback((error: string, context?: Record<string, any>) => {
    trackEvent('error', { error, context }, { persist: false });
  }, [trackEvent]);

  const trackPerformance = useCallback((metrics: Record<string, number>) => {
    trackEvent('performance', metrics);
  }, [trackEvent]);

  // Calculate metrics from events
  const calculateMetrics = useCallback((eventsList: AnalyticsEvent[]): AnalyticsMetrics => {
    const pageViews = eventsList.filter(e => e.event_type === 'page_view').length;
    const uniqueUsers = new Set(eventsList.map(e => e.user_id).filter(Boolean)).size;
    
    // Calculate session duration (simplified)
    const sessions = new Map<string, { start: Date; end: Date }>();
    eventsList.forEach(event => {
      if (event.session_id) {
        const existing = sessions.get(event.session_id);
        if (!existing) {
          sessions.set(event.session_id, { start: event.timestamp, end: event.timestamp });
        } else {
          if (event.timestamp < existing.start) existing.start = event.timestamp;
          if (event.timestamp > existing.end) existing.end = event.timestamp;
        }
      }
    });

    const avgSessionDuration = Array.from(sessions.values()).reduce((sum, session) => {
      return sum + (session.end.getTime() - session.start.getTime());
    }, 0) / sessions.size / 1000; // Convert to seconds

    // Calculate bounce rate (single page view sessions)
    const singlePageSessions = Array.from(sessions.keys()).filter(sessionId => {
      const sessionEvents = eventsList.filter(e => e.session_id === sessionId && e.event_type === 'page_view');
      return sessionEvents.length === 1;
    }).length;
    const bounceRate = (singlePageSessions / sessions.size) * 100;

    // Calculate conversion rate (simplified)
    const conversions = eventsList.filter(e => e.event_type === 'conversion').length;
    const conversionRate = (conversions / uniqueUsers) * 100;

    // Top pages
    const pageViews_map = new Map<string, number>();
    eventsList.filter(e => e.event_type === 'page_view').forEach(event => {
      const page = event.event_data.page || event.page_url || 'unknown';
      pageViews_map.set(page, (pageViews_map.get(page) || 0) + 1);
    });
    const topPages = Array.from(pageViews_map.entries())
      .map(([page, views]) => ({ page, views }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);

    // Device breakdown
    const deviceCounts = new Map<string, number>();
    eventsList.forEach(event => {
      const device = event.device_type || 'unknown';
      deviceCounts.set(device, (deviceCounts.get(device) || 0) + 1);
    });
    const totalEvents = eventsList.length;
    const deviceBreakdown = Array.from(deviceCounts.entries())
      .map(([type, count]) => ({
        type,
        count,
        percentage: (count / totalEvents) * 100
      }))
      .sort((a, b) => b.count - a.count);

    return {
      pageViews,
      uniqueUsers,
      sessionDuration: avgSessionDuration,
      bounceRate,
      conversionRate,
      topPages,
      userFlow: [], // Simplified for now
      deviceBreakdown,
    };
  }, []);

  // Memoized metrics calculation
  const calculatedMetrics = useMemo(() => {
    return calculateMetrics(events);
  }, [events, calculateMetrics]);

  // Update metrics when events change
  useEffect(() => {
    setMetrics(calculatedMetrics);
  }, [calculatedMetrics]);

  // Fetch historical events from database
  const fetchHistoricalEvents = useCallback(async (days: number = 7) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('analytics_events')
        .select('*')
        .gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(1000);
      
      if (error) {
        console.error('Error fetching historical events:', error);
        return;
      }
      
      // Convert database events to AnalyticsEvent format
      const formattedEvents: AnalyticsEvent[] = (data || []).map(event => ({
        id: event.id,
        event_type: event.event_type,
        event_data: (event.event_data as Record<string, any>) || {},
        user_id: event.user_id,
        session_id: event.session_id,
        timestamp: new Date(event.created_at),
        page_url: event.page_url,
        user_agent: event.user_agent,
        device_type: event.device_type as 'mobile' | 'tablet' | 'desktop'
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching historical events:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Export analytics data
  const exportData = useCallback((format: 'json' | 'csv' = 'json') => {
    const data = format === 'json' ? 
      JSON.stringify({ events, metrics }, null, 2) :
      events.map(e => ({
        timestamp: e.timestamp.toISOString(),
        event_type: e.event_type,
        user_id: e.user_id,
        session_id: e.session_id,
        page_url: e.page_url,
        device_type: e.device_type,
        ...e.event_data
      })).map(row => Object.values(row).join(',')).join('\n');

    const blob = new Blob([data], { 
      type: format === 'json' ? 'application/json' : 'text/csv' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [events, metrics]);

  return {
    // Data
    events,
    metrics,
    isLoading,
    currentSession,
    
    // Tracking functions
    trackEvent,
    trackPageView,
    trackUserAction,
    trackConversion,
    trackError,
    trackPerformance,
    
    // Utility functions
    fetchHistoricalEvents,
    exportData,
    calculateMetrics,
  };
};